import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { TaskStatus } from '../task/components/task-status';
import { TaskType, type ImageTaskDto, type VideoTaskDto } from '../task/model';
import { imageCredis, videoCredis } from './api';
import type { CreditConfigPerModelDto } from './model';
import { calcRemovalMilli } from './removal-cost';

function useCreditPerModel(data: CreditConfigPerModelDto[] | undefined) {
  return useMemo(() => {
    if (!data) {
      return;
    }

    return new Map(data.map((item) => [item.model, item]));
  }, [data]);
}

/** 模型配置带 variants 时，按 provider 取变体基准信用点，未命中回退 variants.default */
function resolveVariantCredit(
  configuration: CreditConfigPerModelDto | undefined,
  provider: string | undefined,
) {
  if (configuration?.variants == null) {
    return;
  }

  return (
    (provider != null ? configuration.variants[provider] : undefined) ??
    configuration.variants['default']
  );
}

export function useCalcVideoCost() {
  const { data } = useQuery(videoCredis);

  const creditPerModel = useCreditPerModel(data?.settings);

  return useCallback(
    (task: VideoTaskDto) => {
      if (TaskStatus.isFailed(task.status)) {
        return null;
      }

      if (!creditPerModel) {
        return;
      }

      const configuration = creditPerModel.get(task.model);

      // 视频翻译任务由 generate_data.provider 区分 variants 计费
      const provider =
        task.type === TaskType.视频翻译
          ? task.generate_data?.provider
          : undefined;

      const baseCost =
        resolveVariantCredit(configuration, provider) ??
        configuration?.credit ??
        data?.default;

      if (baseCost == null) {
        return null;
      }

      const duration = Math.trunc(task.duration);
      let costMilli =
        Math.round(baseCost * 1000) *
        (task.type === TaskType.画质修复 ? 1 : duration);

      if (task.type === TaskType.智能擦除 || task.type === TaskType.视频翻译) {
        const removalProvider = task.removal_provider;
        const pricing = removalProvider
          ? creditPerModel.get('app_video_smart_erase')?.removal_pricing?.[
              removalProvider
            ]
          : undefined;
        if (task.type === TaskType.智能擦除) {
          costMilli = pricing
            ? calcRemovalMilli(pricing, duration)
            : Math.round(baseCost * 1000);
        } else if (pricing && duration > 0) {
          costMilli += calcRemovalMilli(pricing, duration);
        }
      }

      return new Intl.NumberFormat('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(costMilli / 1000);
    },
    [creditPerModel, data?.default],
  );
}

export function useCalcImageCost() {
  const { data } = useQuery(imageCredis);

  const creditPerModel = useCreditPerModel(data?.settings);

  return useCallback(
    (task: ImageTaskDto) => {
      if (TaskStatus.isFailed(task.status)) {
        return null;
      }

      if (!creditPerModel) {
        return;
      }

      const cost = creditPerModel.get(task.model)?.credit ?? data?.default;

      if (cost == null) {
        return null;
      }

      return new Intl.NumberFormat('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cost);
    },
    [creditPerModel, data?.default],
  );
}
